import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {type FC, useCallback, useMemo} from 'react';

import {useMemoizedSelector} from '../state/hooks';
import type * as PanelViewTypes from '../state/views/panel/types';
import {type RootState} from '../types/redux';
import {truncateComponentStack} from '../util/datadog/componentStack';
import {recordRumError} from '../util/datadog/datadogRum';
import {type BasePanelProps} from '../util/panelProps';
import {
  getPanelSpec,
  type Panel,
  type PanelConfig,
  type PanelExportRef,
} from '../util/panelTypes';
import {ErrorBoundary} from './ErrorBoundary';
import {useSingleRunModeContext} from './MultiRunWorkspace/SingleRunMode';
import {useInheritedSettings} from './Panel/hooks/useInheritedSettings';
import {
  usePanelSharedProps,
  usePanelSharedPropsForPanel,
} from './Panel/hooks/usePanelSharedProps';
import {
  recordPanelErrored,
  useRecordPanelRendered,
} from './Panel/panelAvailability';
import {useStablePanelId} from './Panel/PanelIDContext';
import {usePanelSurface} from './Panel/PanelSurfaceContext';
import {default as PanelRunsLinePlot} from './PanelRunsLinePlot/ComponentWrapper';
import {InstrumentedCenteredWaveLoader as Loader} from './utility/InstrumentedLoader';
import {
  useMediaBrowserPanelWithCascadedConfig,
  usePanelWithInheritedSettings,
} from './WorkspaceDrawer/Settings/hooks/usePanelInheritedSettings';
import {useSectionSettingsContextMaybe} from './WorkspaceDrawer/Settings/SectionSettingsContextProvider';

type PanelCompProps = Parameters<typeof PanelComp>[0];
type PanelCompReduxProps = Omit<
  PanelCompProps,
  | 'panel'
  | 'updateConfig'
  | 'data'
  | 'loading'
  | 'runsDataQuery'
  | 'query'
  | 'pageQuery'
  | 'customRunColors'
  | 'customRunNames'
> & {
  panelRef: PanelViewTypes.Ref;
  panelExportRef?: React.MutableRefObject<PanelExportRef | undefined>;
};

const PanelComp = (
  props: BasePanelProps & {
    panel: Panel;
    panelExportRef?: React.MutableRefObject<PanelExportRef | undefined>;
    updateConfig(
      newConfig: PanelConfig,
      options?: {isUndoable?: boolean}
    ): void;
  }
) => {
  const {panel, updateConfig, ...passThroughProps} = props;
  const {isSingleRun} = useSingleRunModeContext();

  const {
    Component: PanelComponent,
    transformConfig,
    transformConfigUpdate,
  } = getPanelSpec(panel.viewType);

  const configTransformed = useMemo(
    () => transformConfig?.(panel.config as any, {isSingleRun}) ?? panel.config,
    [panel.config, transformConfig, isSingleRun]
  );

  const transformAndUpdateConfig = useCallback(
    (newConfig: PanelConfig, options?: {isUndoable?: boolean}) => {
      const newConfigTransformed =
        transformConfigUpdate != null
          ? transformConfigUpdate(newConfig as any, {isSingleRun})
          : newConfig;
      updateConfig(newConfigTransformed, options);
    },
    [updateConfig, transformConfigUpdate, isSingleRun]
  );

  /**
   * Typically we lazy load the panel components, but this creates a double loading state for full fidelity runs line plots because each panel has to mount in order to dispatch its queries (which it now owns instead of the run selector). Directly importing this component and mounting it without suspense gets rid of the redundant loading state and saves users a quick blink and some layout shift in the underlying loader animation.
   */
  if (panel.viewType === 'Run History Line Plot') {
    return (
      <PanelRunsLinePlot
        {...passThroughProps}
        config={configTransformed as any}
        updateConfig={
          transformAndUpdateConfig as (
            config: any,
            options?: {isUndoable?: boolean}
          ) => void
        }
      />
    );
  }

  return (
    <React.Suspense
      fallback={
        <div className="loader-wrapper">
          <Loader name={'panel-comp-suspense-loader'} size="huge" />
        </div>
      }>
      <PanelComponent
        {...passThroughProps}
        // Here we know that s.transformQuery can be called with panel.config, because
        // spec.type === panel.viewType. However, there's no way to get typescript
        // to narrow a union to an invidual member, so we just cast to any.
        config={configTransformed as any}
        updateConfig={
          transformAndUpdateConfig as (
            config: any,
            options?: {isUndoable?: boolean}
          ) => void
        }
      />
    </React.Suspense>
  );
};

const makePanelViewTypeSelector =
  (panelRef: PanelViewTypes.Ref) => (state: RootState) =>
    state.views.parts[panelRef.type][panelRef.id]?.viewType;

export const PanelCompRedux: FC<PanelCompReduxProps> = props => {
  const viewType = useMemoizedSelector(
    makePanelViewTypeSelector,
    props.panelRef
  );
  useRecordPanelRendered(viewType);
  // The boundary wraps the components below rather than living inside them:
  // they load the panel's runs data during their own render, and a component
  // cannot catch what it throws itself. Without this placement a failed panel
  // query escapes to the workspace boundary and takes down the whole page.
  return (
    <PanelErrorBoundary viewType={viewType}>
      {viewType === 'Media Browser' ? (
        <MediaBrowserPanelCompRedux {...props} />
      ) : (
        <DefaultPanelCompRedux {...props} />
      )}
    </PanelErrorBoundary>
  );
};

const PanelErrorBoundary: FC<{
  viewType: string | undefined;
  children: React.ReactNode;
}> = ({viewType, children}) => {
  const panelSurface = usePanelSurface();
  const logError = useCallback(
    (error: Error, info: React.ErrorInfo) => {
      // A failed panel renders its own error state next to the working panels,
      // so it must not open the full-page crash report dialog. The boundary is
      // `silent` for that reason, and the log is repeated here to keep the
      // console message for debugging.
      console.error('Encountered ErrorBoundary:', error);
      // Send to Segment (view in Mixpanel)
      window.analytics?.track('Panel Error', {
        viewType,
        panelSurface,
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack,
      });
      recordRumError(error, {
        message: 'Panel ErrorBoundary error',
        panelViewType: viewType,
        panelSurface,
        componentStack: truncateComponentStack(info.componentStack),
      });
      if (viewType != null) {
        recordPanelErrored(viewType, 'render_crash');
      }
    },
    [viewType, panelSurface]
  );
  const stablePanelId = useStablePanelId();

  return (
    <ErrorBoundary
      silent
      onError={logError}
      renderError={() => renderPanelError(stablePanelId, viewType ?? '')}>
      {children}
    </ErrorBoundary>
  );
};

const DefaultPanelCompRedux: FC<PanelCompReduxProps> = props => {
  const inheritedSettings = useInheritedSettings();
  const panel = usePanelWithInheritedSettings(
    props.panelRef,
    inheritedSettings
  );
  const sharedProps = usePanelSharedProps(props.panelRef);
  return <PanelCompBody {...props} panel={panel} sharedProps={sharedProps} />;
};

/**
 * Media Browser panels read their cascaded config through per-field Redux
 * selectors rather than PanelContext's `inheritedSettings`, so they don't
 * re-render when unrelated (e.g. line plot) settings change.
 */
const MediaBrowserPanelCompRedux: FC<PanelCompReduxProps> = props => {
  const panel = useMediaBrowserPanelWithCascadedConfig(props.panelRef);
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const sharedProps = usePanelSharedPropsForPanel(
    props.panelRef,
    panel,
    sectionRef
  );
  return <PanelCompBody {...props} panel={panel} sharedProps={sharedProps} />;
};

const PanelCompBody: FC<
  PanelCompReduxProps & {
    panel: Panel;
    sharedProps: ReturnType<typeof usePanelSharedProps>;
  }
> = props => {
  const {panel, panelExportRef, sharedProps, ...restProps} = props;

  return (
    <PanelComp
      {...restProps}
      {...sharedProps}
      panel={panel}
      panelExportRef={panelExportRef}
    />
  );
};

function renderPanelError(panelID: string, panelViewType: string) {
  return (
    <div style={{padding: 40}}>
      <PanelError
        panelID={panelID}
        panelViewType={panelViewType}
        message={
          <div>
            Oops, something went wrong. If this keeps happening, message
            support@wandb.com with a link to this page
          </div>
        }
        logErrorMessage="Oops, something went wrong. If this keeps happening, message support@wandb.com with a link to this page"
      />
    </div>
  );
}
